import { createApi, fetchBaseQuery } from "@reduxjs/toolkit/query/react";
import type { BaseQueryFn, FetchArgs, FetchBaseQueryError } from "@reduxjs/toolkit/query/react";

import config from "../../components/_constants/config";
import { readAuthToken } from "../../utils/authToken";
import { notifyUnauthorized } from "../../utils/unauthorized";

const rawBaseQuery = fetchBaseQuery({
  baseUrl: config.REACT_APP_API_URL,
  // Read through `utils/authToken` — the same store `useApi` reads — rather than a
  // `localStorage.accessToken` that only this file knew about. The header is left off entirely
  // when there is no session, instead of sending a literal "Bearer undefined".
  prepareHeaders: headers => {
    const token = readAuthToken();

    if (token) headers.set("Authorization", `Bearer ${token}`);

    return headers;
  },
});

// Same rule as `useApi`: an expired or missing token ends the session instead of surfacing an
// error the page can do nothing about.
const baseQuery: BaseQueryFn<string | FetchArgs, unknown, FetchBaseQueryError> = async (
  args,
  api,
  extraOptions
) => {
  const result = await rawBaseQuery(args, api, extraOptions);

  if (result.error?.status === 401) {
    notifyUnauthorized();
  }

  return result;
};

/**
 * The single RTK Query API. Domain modules in this folder add to it with
 * `baseApi.injectEndpoints(...)`, so the store keeps one reducer, one middleware
 * and one cache no matter how many domains are added.
 */
export const baseApi = createApi({
  reducerPath: "api",
  baseQuery,
  endpoints: () => ({}),
});
